import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  ALLOWED_LEAD_MINUTES,
  currentPermissionState,
  forgetPreferences,
  iosNeedsHomeScreenInstall,
  readAnalyticsOptOut,
  readLeadMinutes,
  requestPermission,
  showTestNotification,
  writeAnalyticsOptOut,
  writeLeadMinutes,
  type AllowedLeadMinutes,
  type NotificationPermissionState,
} from '../notify/NotifyService';
import { prefersReducedMotion } from '../util/motion';
import { getSharedObserver } from '../observer/Observer';

/**
 * Phase 8 chunk 5B — <sat-settings> topbar element.
 *
 * Gear ⚙ button anchored at the end of the topbar action cluster
 * per docs/phase8.md § II row 13. (The originally-adjacent theme
 * switcher was retired post-Phase-11 — dark is now the only theme.)
 * Click opens a panel exposing:
 *   - Notification permission + lead-time radio (2/5/10/15 min) +
 *     "Test notification" button — preferences persist via
 *     {@link NotifyService}
 *   - Accessibility row showing the live `prefers-reduced-motion`
 *     state (read-only — actual gates live in chunk 1)
 *   - Privacy row with the analytics opt-out toggle that gates the
 *     Plausible tag in chunk 6
 *   - "Forget my preferences" button — clears NotifyService prefs +
 *     the saved observer location
 *
 * Closes on outside click, Escape, or selecting any in-panel action
 * (matching the OverlaysMenu pattern).
 */
@customElement('sat-settings')
export class SatSettings extends LitElement {
  @state() private open = false;
  @state() private permission: NotificationPermissionState = 'unsupported';
  @state() private leadMinutes: AllowedLeadMinutes = 5;
  @state() private analyticsOptOut = false;
  @state() private reducedMotion = false;
  @state() private iosBlocked = false;
  /** Inline status for the Test/Enable buttons. */
  @state() private statusNote: string | null = null;
  /** Post-Phase-11 — controls the About modal child element. */
  @state() private aboutOpen = false;

  private outsideClickHandler = (e: MouseEvent): void => {
    if (!this.open) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.open = false;
    }
  };

  private keydownHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this.open = false;
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.outsideClickHandler);
    document.addEventListener('keydown', this.keydownHandler);
    this.refreshFromPrefs();
  }

  disconnectedCallback(): void {
    document.removeEventListener('click', this.outsideClickHandler);
    document.removeEventListener('keydown', this.keydownHandler);
    super.disconnectedCallback();
  }

  private refreshFromPrefs(): void {
    this.permission = currentPermissionState();
    this.leadMinutes = readLeadMinutes();
    this.analyticsOptOut = readAnalyticsOptOut();
    this.reducedMotion = prefersReducedMotion();
    this.iosBlocked = iosNeedsHomeScreenInstall();
  }

  static styles = css`
    :host {
      position: relative;
      display: inline-block;
      font-family: var(--font-mono);
    }
    .toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.6rem;
      background: transparent;
      color: var(--color-text);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 0.85rem;
      cursor: pointer;
    }
    .toggle:hover { border-color: var(--color-accent); color: var(--color-accent); }
    .toggle[aria-expanded='true'] { border-color: var(--color-accent); color: var(--color-accent); }
    .toggle__glyph { font-size: 0.95rem; line-height: 1; }

    /* Collapse the "Settings" label below 1300px — matches the rest of
       the action pills (Overlays, Share, View toggle). */
    @media (max-width: 1300px) {
      .toggle__label { display: none; }
    }

    .menu {
      position: absolute;
      top: calc(100% + 0.4rem);
      right: 0;
      min-width: 320px;
      background: var(--color-bg-elevated);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 0.4rem 0;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
      z-index: 100;
    }
    .menu h3 {
      margin: 0;
      padding: 0.4rem 0.8rem 0.3rem;
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-text-dim);
    }
    .menu h3:not(:first-child) {
      border-top: 1px solid var(--color-border);
      margin-top: 0.3rem;
    }

    .row {
      padding: 0.35rem 0.8rem;
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      font-size: 0.8rem;
      color: var(--color-text);
    }
    .row__label {
      font-size: 0.78rem;
      color: var(--color-text);
    }
    .row__hint {
      font-size: 0.7rem;
      color: var(--color-text-dim);
      font-style: italic;
    }
    .row__hint--warn {
      color: var(--color-warning);
      font-style: normal;
    }

    .lead-radio {
      display: flex;
      gap: 0.3rem;
    }
    .lead-radio button {
      flex: 1;
      padding: 0.25rem 0.4rem;
      background: transparent;
      border: 1px solid var(--color-border);
      color: var(--color-text-muted);
      font-family: var(--font-mono);
      font-size: 0.78rem;
      border-radius: 3px;
      cursor: pointer;
    }
    .lead-radio button[aria-pressed='true'] {
      background: var(--color-accent);
      color: var(--color-bg);
      border-color: var(--color-accent);
    }
    .lead-radio button:hover { border-color: var(--color-accent); }

    .action {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.6rem;
      background: transparent;
      border: 1px solid var(--color-border);
      color: var(--color-text);
      font-family: var(--font-mono);
      font-size: 0.78rem;
      border-radius: 3px;
      cursor: pointer;
    }
    .action:hover { border-color: var(--color-accent); color: var(--color-accent); }
    .action--danger:hover { border-color: var(--color-danger); color: var(--color-danger); }

    .toggle-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }
    .toggle-row input[type='checkbox'] {
      accent-color: var(--color-accent);
      width: 1rem;
      height: 1rem;
    }

    .status-pill {
      font-family: var(--font-mono);
      font-size: 0.7rem;
      padding: 0.05rem 0.4rem;
      border-radius: 3px;
      border: 1px solid currentColor;
    }
    .status-pill--granted { color: var(--color-accent); }
    .status-pill--denied,
    .status-pill--unsupported { color: var(--color-warning); }
    .status-pill--default { color: var(--color-text-muted); }
  `;

  private toggleOpen(e: Event): void {
    e.stopPropagation();
    this.open = !this.open;
    this.statusNote = null;
    if (this.open) this.refreshFromPrefs();
  }

  private async handleEnable(): Promise<void> {
    if (this.iosBlocked) {
      this.statusNote = 'iOS requires Add to Home Screen first — open this page from the home-screen icon and try again.';
      return;
    }
    this.permission = await requestPermission();
    this.statusNote = this.permission === 'granted'
      ? 'Notifications enabled.'
      : this.permission === 'denied'
        ? 'Browser blocked notifications. Update the site permission in your browser to retry.'
        : this.permission === 'unsupported'
          ? "This browser doesn't support the Notifications API."
          : null;
  }

  private async handleTest(): Promise<void> {
    if (this.permission !== 'granted') {
      this.statusNote = 'Click "Enable" first so the browser can show a sample notification.';
      return;
    }
    const result = await showTestNotification();
    this.statusNote = result === 'granted'
      ? 'Test fired. Check your OS notification tray.'
      : 'Failed to fire the test notification.';
  }

  private handleLeadChoice(value: AllowedLeadMinutes): void {
    writeLeadMinutes(value);
    this.leadMinutes = value;
    this.statusNote = `Lead time set to ${value} min.`;
  }

  private handleAnalyticsToggle(e: Event): void {
    const next = (e.target as HTMLInputElement).checked;
    writeAnalyticsOptOut(next);
    this.analyticsOptOut = next;
  }

  private handleForget(): void {
    forgetPreferences();
    getSharedObserver().clear();
    this.refreshFromPrefs();
    this.statusNote = 'Cleared lead time, analytics opt-out, and observer location.';
  }

  private renderPermissionPill() {
    const label = this.permission === 'granted' ? 'Granted'
      : this.permission === 'denied' ? 'Blocked'
      : this.permission === 'unsupported' ? 'Unsupported'
      : 'Not asked';
    return html`<span class="status-pill status-pill--${this.permission}">${label}</span>`;
  }

  private renderNotificationsSection() {
    return html`
      <h3>§ Notifications</h3>
      <div class="row">
        <div class="toggle-row">
          <span class="row__label">Permission</span>
          ${this.renderPermissionPill()}
        </div>
        ${this.iosBlocked
          ? html`<div class="row__hint row__hint--warn">
              iOS requires Add to Home Screen first. Tap Share → Add to Home Screen, then reopen from the new icon.
            </div>`
          : nothing}
        <div style="display: flex; gap: 0.4rem; flex-wrap: wrap;">
          <button
            class="action"
            type="button"
            @click=${this.handleEnable}
            ?disabled=${this.permission === 'granted' || this.permission === 'unsupported'}
          >Enable</button>
          <button
            class="action"
            type="button"
            @click=${this.handleTest}
            ?disabled=${this.permission !== 'granted'}
          >🔔 Test notification</button>
        </div>
      </div>
      <div class="row">
        <span class="row__label">Lead time before rise</span>
        <div class="lead-radio" role="radiogroup" aria-label="Notification lead time">
          ${ALLOWED_LEAD_MINUTES.map((m) => html`
            <button
              type="button"
              role="radio"
              aria-pressed=${this.leadMinutes === m ? 'true' : 'false'}
              @click=${() => this.handleLeadChoice(m)}
            >${m} min</button>
          `)}
        </div>
        <span class="row__hint">Default 5 min — fires this far before the pass rises above the horizon.</span>
      </div>
    `;
  }

  private renderAccessibilitySection() {
    return html`
      <h3>§ Accessibility</h3>
      <div class="row">
        <div class="toggle-row">
          <span class="row__label">Reduced motion</span>
          <span class="status-pill ${this.reducedMotion ? 'status-pill--granted' : 'status-pill--default'}">
            ${this.reducedMotion ? 'Detected' : 'Not set'}
          </span>
        </div>
        <span class="row__hint">
          Read from your OS. When detected, dot-pulse + camera flights + panel slide animations all become instant.
        </span>
      </div>
    `;
  }

  private renderPrivacySection() {
    return html`
      <h3>§ Privacy</h3>
      <div class="row">
        <label class="toggle-row" style="cursor: pointer;">
          <span class="row__label">Disable analytics</span>
          <input
            type="checkbox"
            .checked=${this.analyticsOptOut}
            @change=${this.handleAnalyticsToggle}
          />
        </label>
        <span class="row__hint">
          Suppresses the Plausible script tag on every page. Honored alongside your browser's Do-Not-Track signal.
        </span>
      </div>
    `;
  }

  private renderResetSection() {
    return html`
      <h3>§ Reset</h3>
      <div class="row">
        <button class="action action--danger" type="button" @click=${this.handleForget}>
          Forget my preferences
        </button>
        <span class="row__hint">Clears lead-time, analytics opt-out, and saved observer location.</span>
      </div>
    `;
  }

  private renderAboutSection() {
    return html`
      <h3>§ About</h3>
      <div class="row">
        <button
          class="action"
          type="button"
          data-test="about-trigger"
          @click=${this.openAbout}
        >About sat.trackr.live</button>
        <span class="row__hint">Version, license, and the last time we pulled fresh orbital data.</span>
      </div>
    `;
  }

  private openAbout(): void {
    this.aboutOpen = true;
    // Close the settings popover so the modal isn't visually stacked
    // behind it; the user re-opens settings via the gear when done.
    this.open = false;
  }

  private closeAbout(): void {
    this.aboutOpen = false;
  }

  render() {
    return html`
      <button
        class="toggle"
        type="button"
        aria-haspopup="dialog"
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-label="Settings"
        @click=${this.toggleOpen}
      >
        <span class="toggle__glyph" aria-hidden="true">⚙</span>
        <span class="toggle__label">Settings</span>
      </button>
      ${this.open ? html`
        <div class="menu" role="dialog" aria-label="Settings panel">
          ${this.renderNotificationsSection()}
          ${this.renderAccessibilitySection()}
          ${this.renderPrivacySection()}
          ${this.renderAboutSection()}
          ${this.renderResetSection()}
          ${this.statusNote !== null ? html`
            <div class="row" data-status-line>
              <span class="row__hint" style="font-style: normal; color: var(--color-text);">${this.statusNote}</span>
            </div>
          ` : nothing}
        </div>
      ` : nothing}
      <sat-about-modal
        .open=${this.aboutOpen}
        @close=${this.closeAbout}
      ></sat-about-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-settings': SatSettings;
  }
}
